import { processPendingScans } from './syncQueueProcessor';
import { INITIAL_SYNC_STATUS, type SyncStatus } from './types';

const BASE_INTERVAL_MS = 10_000; // spec: "every 10 seconds"
const MAX_BACKOFF_MULTIPLIER = 6; // caps out at 60s between attempts during a prolonged outage
const BACKOFF_THRESHOLD = 3; // start backing off only after this many consecutive whole-batch failures

let status: SyncStatus = { ...INITIAL_SYNC_STATUS };
const listeners = new Set<(status: SyncStatus) => void>();

let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
let onlineListenerAttached = false;

function emit(): void {
  listeners.forEach((listener) => listener(status));
}

function updateStatus(patch: Partial<SyncStatus>): void {
  status = { ...status, ...patch };
  emit();
}

function currentIntervalMs(): number {
  if (status.consecutiveFailures < BACKOFF_THRESHOLD) {
    return BASE_INTERVAL_MS;
  }

  const multiplier = Math.min(
    MAX_BACKOFF_MULTIPLIER,
    status.consecutiveFailures - BACKOFF_THRESHOLD + 2
  );
  return BASE_INTERVAL_MS * multiplier;
}

async function tick(): Promise<void> {
  if (status.isSyncing || !navigator.onLine) {
    return;
  }

  updateStatus({ isSyncing: true, isOnline: true, lastAttemptAtUtc: new Date().toISOString() });

  try {
    const result = await processPendingScans();

    if (result.hadTransientFailure) {
      updateStatus({
        isSyncing: false,
        lastError: result.transientFailureMessage,
        consecutiveFailures: status.consecutiveFailures + 1
      });
    } else {
      updateStatus({
        isSyncing: false,
        lastError: null,
        consecutiveFailures: 0,
        ...(result.attempted > 0 ? { lastSuccessAtUtc: new Date().toISOString() } : {})
      });
    }
  } catch (err) {
    // processPendingScans is designed not to throw (failures are caught
    // per-batch), but guard anyway so a truly unexpected error can't leave
    // isSyncing stuck true forever, silently halting all future sync ticks.
    updateStatus({
      isSyncing: false,
      lastError: err instanceof Error ? err.message : 'Unexpected sync error.',
      consecutiveFailures: status.consecutiveFailures + 1
    });
  }
}

function scheduleNextTick(): void {
  if (timeoutHandle !== null) {
    clearTimeout(timeoutHandle);
  }

  timeoutHandle = setTimeout(async () => {
    await tick();
    // Re-evaluate the delay AFTER each tick so backoff (or recovery from
    // it) takes effect on the very next cycle without needing an engine
    // restart. Recursive setTimeout (rather than setInterval) guarantees
    // exactly one pending timer at a time, with no risk of overlapping
    // fires if a tick ever took longer than the interval itself.
    scheduleNextTick();
  }, currentIntervalMs());
}

function handleOnline(): void {
  updateStatus({ isOnline: true });
  // Don't wait for the next scheduled tick -- sync immediately on reconnect,
  // per the "synchronises automatically when a connection becomes
  // available" requirement.
  void tick();
}

function handleOffline(): void {
  updateStatus({ isOnline: false });
}

export const syncEngine = {
  start(): void {
    if (timeoutHandle !== null) {
      return; // already running
    }

    if (!onlineListenerAttached) {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      onlineListenerAttached = true;
    }

    updateStatus({ isOnline: navigator.onLine });
    void tick(); // attempt immediately on start rather than waiting a full interval
    scheduleNextTick();
  },

  stop(): void {
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle);
      timeoutHandle = null;
    }

    if (onlineListenerAttached) {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      onlineListenerAttached = false;
    }
  },

  /** Manual trigger -- e.g. a "sync now" pull-to-refresh affordance. */
  triggerNow(): void {
    void tick();
  },

  subscribe(listener: (status: SyncStatus) => void): () => void {
    listeners.add(listener);
    listener(status); // immediately push current state to a new subscriber
    return () => listeners.delete(listener);
  },

  getStatus(): SyncStatus {
    return status;
  }
};
